/**
 * DatePicker — 단일 날짜 선택기 (Segmented 입력 + 팝업 캘린더).
 *
 * UX:
 *   [YYYY] / [MM] / [DD]  📅
 *   - 연도 4자리 타이핑 시 자동 월 input으로 focus + 전체 select
 *   - 월 2자리 타이핑 시 자동 일 input으로 focus + 전체 select
 *   - 일 2자리 타이핑 시 commit
 *   - `/`, `-`, `.` 입력 시 다음 segment로 이동
 *   - Backspace 빈 input → 이전 segment 끝 커서로 이동
 *   - Enter → commit + popup 닫기
 *   - Esc → popup 닫기 + 직전 valid 값 복원
 *   - 외부 클릭 → commit + popup 닫기
 *   - 📅 클릭 → 캘린더 popup toggle
 *   - inputMode="numeric" (모바일 숫자 키패드)
 *   - min/max 위반 시 빨간 테두리 + commit 거부
 *
 * 모든 날짜 문자열은 YYYY-MM-DD.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import {
  WEEKDAYS,
  buildCalendarCells,
  cmp,
  daysInMonth,
  fmtDate,
  pad,
  parseDate,
  todayStr,
} from "./dateUtils";

export interface DatePickerProps {
  /** YYYY-MM-DD 또는 빈 문자열 */
  value: string;
  onChange: (value: string) => void;
  /** YYYY-MM-DD 이상 (선택 가능 최소일) */
  min?: string;
  /** YYYY-MM-DD 이하 (선택 가능 최대일) */
  max?: string;
  /** 연도 segment 셀렉트 범위 */
  minYear?: number;
  maxYear?: number;
  fullWidth?: boolean;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  /** 마운트 시 연도 input에 focus */
  autoFocus?: boolean;
  /**
   * 휴일 마킹 콜백 — 날짜(YYYY-MM-DD)가 지정 휴일이면 사유 라벨(공휴일명 등),
   * 아니면 null 반환. 반환값이 있으면 캘린더 셀에 휴일 점 + 툴팁 표시.
   * 미전달 시 마킹 없음(기본 동작 불변).
   */
  holidayLabel?: (ymd: string) => string | null;
}

/**
 * CompactSelect — DatePicker 캘린더 헤더 전용 작은 dropdown.
 *
 * 시스템 Select가 height 40px 고정이라 캘린더 헤더에 너무 큼.
 * 동일 시각 언어(흰 배경/체크 dropdown/하이라이트 active)지만 compact 사이즈.
 *
 * 키보드 지원:
 *   - ↑↓: 옵션 이동
 *   - 숫자 입력: type-ahead. 라벨이 누적 버퍼로 시작하는 첫 옵션으로 이동
 *                (800ms 후 버퍼 리셋)
 *   - Enter: 현재 focused 옵션 확정 + 닫기
 *   - Esc: 닫기 (값 변경 없음)
 *   - Home/End: 처음/마지막 옵션
 */
interface CompactSelectProps {
  value: number;
  options: { value: number; label: string }[];
  onChange: (value: number) => void;
  ariaLabel?: string;
}

export function CompactSelect({ value, options, onChange, ariaLabel }: CompactSelectProps) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState<number>(-1);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const bufferRef = useRef<string>("");
  const bufferTimerRef = useRef<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  // open 토글 시 초기 focusIdx 설정 + 버퍼 리셋 + 외부 클릭 핸들러
  useEffect(() => {
    if (!open) {
      bufferRef.current = "";
      if (bufferTimerRef.current) {
        clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = null;
      }
      setMenuPos(null);
      return;
    }
    const idx = options.findIndex((o) => o.value === value);
    setFocusIdx(idx >= 0 ? idx : 0);

    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, options, value]);

  // 위치 측정 + 스크롤/리사이즈 대응
  useEffect(() => {
    if (!open) return;
    function updatePos() {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      setMenuPos({ top: r.bottom + 4, left: r.left, minWidth: r.width });
    }
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  // 키보드 핸들러 (open 동안 활성)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (focusIdx >= 0 && focusIdx < options.length) {
          onChange(options[focusIdx].value);
          setOpen(false);
          triggerRef.current?.focus();
        }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(options.length - 1, i < 0 ? 0 : i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i < 0 ? options.length - 1 : i - 1));
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        setFocusIdx(0);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        setFocusIdx(options.length - 1);
        return;
      }
      // 숫자 타입어헤드 — 라벨 prefix 매칭
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        if (bufferTimerRef.current) clearTimeout(bufferTimerRef.current);
        bufferRef.current += e.key;
        const buf = bufferRef.current;
        const idx = options.findIndex((o) => o.label.startsWith(buf));
        if (idx >= 0) setFocusIdx(idx);
        bufferTimerRef.current = window.setTimeout(() => {
          bufferRef.current = "";
        }, 800);
        return;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, focusIdx, options, onChange]);

  // focusIdx 변경 시 해당 옵션을 보이게 스크롤
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const items = menuRef.current.querySelectorAll(".dp-cs-opt");
    const target = items[focusIdx] as HTMLElement | undefined;
    if (target) target.scrollIntoView({ block: "nearest" });
  }, [open, focusIdx]);

  const current = options.find((o) => o.value === value);

  return (
    <div className={`dp-cs${open ? " is-open" : ""}`} ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className="dp-cs-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{current?.label ?? String(value)}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && menuPos && createPortal(
        <div
          className="dp-cs-menu is-portal"
          ref={menuRef}
          role="listbox"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            minWidth: menuPos.minWidth,
          }}
        >
          {options.map((o, i) => {
            const isActive = o.value === value;
            const isFocused = i === focusIdx;
            const cls = [
              "dp-cs-opt",
              isActive ? "is-active" : "",
              isFocused && !isActive ? "is-focused" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isActive}
                className={cls}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  minYear = 2013,
  maxYear,
  fullWidth = false,
  disabled = false,
  id,
  ariaLabel,
  autoFocus = false,
  holidayLabel,
}: DatePickerProps) {
  const today = useMemo(() => todayStr(), []);
  const maxY = maxYear ?? new Date().getFullYear() + 2;

  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const yRef = useRef<HTMLInputElement>(null);
  const mRef = useRef<HTMLInputElement>(null);
  const dRef = useRef<HTMLInputElement>(null);

  /** popup 위치 — body 레벨로 portal 렌더링하므로 fixed 좌표 */
  const [popupPos, setPopupPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  // segment text states (string, 사용자 타이핑 진행 중 상태 보존)
  // ⚠ 초기값을 value 에서 뽑는다 — 빈 문자열로 두면 아래 useEffect 가 첫 페인트 **뒤**에
  //   돌기 때문에 진입 순간 "YYYY / MM / DD" 가 한 프레임 보인다.
  const initSeg = parseDate(value);
  const [ys, setYs] = useState(initSeg ? String(initSeg.y) : "");
  const [ms, setMs] = useState(initSeg ? pad(initSeg.m) : "");
  const [ds, setDs] = useState(initSeg ? pad(initSeg.d) : "");

  /** 외부 value 변경 → segment sync (편집 중이 아닐 때만) */
  const editingRef = useRef(false);

  // autoFocus
  useEffect(() => {
    if (autoFocus) {
      yRef.current?.focus();
      yRef.current?.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (editingRef.current) return;
    const p = parseDate(value);
    if (p) {
      setYs(String(p.y));
      setMs(pad(p.m));
      setDs(pad(p.d));
      setError(false);
    } else {
      setYs("");
      setMs("");
      setDs("");
      setError(false);
    }
  }, [value]);

  /** 캘린더 popup이 표시하는 월 */
  const [popupYM, setPopupYM] = useState(() => {
    const p = parseDate(value) ?? parseDate(today)!;
    return { y: p.y, m: p.m };
  });
  // popup 열릴 때마다 현재 value(또는 오늘)로 동기화
  useEffect(() => {
    if (!open) return;
    const p = parseDate(value) ?? parseDate(today)!;
    setPopupYM({ y: p.y, m: p.m });
  }, [open, value, today]);

  // 외부 클릭 → 닫기 + commit. portal로 떠 있는 popup도 "내부"로 인정.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      // 연/월 CompactSelect 메뉴는 body로 portal됨 → 외부 클릭으로 오인해 닫지 않도록 예외
      if (target instanceof Element && target.closest(".dp-cs-menu")) return;
      commit();
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ys, ms, ds]);

  // popup 위치 측정 — 아래 공간이 부족하면 입력칸 위로 뒤집어 띄운다(모달 하단에서 잘림 방지)
  const updatePos = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 6;
    // popup이 아직 마운트 전이면 캘린더 기본 높이(약 340px)로 추정, 마운트 후엔 실측
    const popupH = popupRef.current?.offsetHeight ?? 340;
    const popupW = popupRef.current?.offsetWidth ?? Math.max(rect.width, 240);
    const spaceBelow = window.innerHeight - rect.bottom;
    // 아래가 부족하고 위가 더 넓으면 위로 flip
    const top =
      spaceBelow < popupH + margin + 8 && rect.top > spaceBelow
        ? Math.max(8, rect.top - margin - popupH)
        : rect.bottom + margin;
    // 좌우 뷰포트 밖으로 나가지 않게 clamp
    let left = rect.left;
    if (left + popupW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - popupW);
    setPopupPos({ top, left, minWidth: rect.width });
  }, []);

  useEffect(() => {
    if (!open) {
      setPopupPos(null);
      return;
    }
    updatePos();
    // popup 마운트 후 실제 높이로 재측정(flip 정확도)
    const raf = requestAnimationFrame(updatePos);
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  /** 3개 segment가 valid YYYY-MM-DD 만들면 onChange */
  function commit(): boolean {
    editingRef.current = false;
    const y = Number(ys);
    const m = Number(ms);
    const d = Number(ds);
    const allEmpty = !ys && !ms && !ds;
    if (allEmpty) {
      if (value !== "") onChange("");
      setError(false);
      return true;
    }
    const yOk = ys.length === 4 && Number.isFinite(y) && y >= minYear && y <= maxY;
    const mOk = ms.length >= 1 && m >= 1 && m <= 12;
    const dOk = ds.length >= 1 && d >= 1 && d <= daysInMonth(y, m);
    if (!yOk || !mOk || !dOk) {
      // 마지막 valid value로 복원
      const p = parseDate(value);
      if (p) {
        setYs(String(p.y));
        setMs(pad(p.m));
        setDs(pad(p.d));
      }
      setError(false);
      return false;
    }
    const next = fmtDate(y, m, d);
    if (min && cmp(next, min) < 0) {
      setError(true);
      return false;
    }
    if (max && cmp(next, max) > 0) {
      setError(true);
      return false;
    }
    setError(false);
    if (next !== value) onChange(next);
    return true;
  }

  function focusNext(from: "y" | "m" | "d") {
    if (from === "y") {
      mRef.current?.focus();
      mRef.current?.select();
    } else if (from === "m") {
      dRef.current?.focus();
      dRef.current?.select();
    }
  }

  function focusPrev(from: "m" | "d") {
    if (from === "m") {
      yRef.current?.focus();
      const len = (yRef.current?.value ?? "").length;
      yRef.current?.setSelectionRange(len, len);
    } else if (from === "d") {
      mRef.current?.focus();
      const len = (mRef.current?.value ?? "").length;
      mRef.current?.setSelectionRange(len, len);
    }
  }

  function onSegChange(seg: "y" | "m" | "d", raw: string) {
    editingRef.current = true;
    const clean = raw.replace(/\D/g, "");
    const maxLen = seg === "y" ? 4 : 2;
    const next = clean.slice(0, maxLen);
    if (seg === "y") setYs(next);
    else if (seg === "m") setMs(next);
    else setDs(next);

    // auto-advance
    if (next.length === maxLen) {
      const n = Number(next);
      if (seg === "y" && n >= minYear && n <= maxY) {
        focusNext("y");
      } else if (seg === "m" && n >= 1 && n <= 12) {
        focusNext("m");
      } else if (seg === "d") {
        const yy = Number(ys);
        const mm = Number(ms);
        if (yy && mm && n >= 1 && n <= daysInMonth(yy, mm)) {
          // 3개 모두 valid면 commit
          const candidate = fmtDate(yy, mm, n);
          if ((!min || cmp(candidate, min) >= 0) && (!max || cmp(candidate, max) <= 0)) {
            editingRef.current = false;
            if (candidate !== value) onChange(candidate);
            setError(false);
          }
        }
      }
    }
  }

  function onSegKeyDown(seg: "y" | "m" | "d", e: React.KeyboardEvent<HTMLInputElement>) {
    const target = e.currentTarget;
    // 구분자 키 → 다음 segment
    if (e.key === "/" || e.key === "-" || e.key === ".") {
      e.preventDefault();
      if (seg === "y") focusNext("y");
      else if (seg === "m") focusNext("m");
      return;
    }
    // Backspace at empty → prev
    if (e.key === "Backspace" && target.value === "" && target.selectionStart === 0) {
      if (seg === "m") focusPrev("m");
      else if (seg === "d") focusPrev("d");
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const ok = commit();
      if (ok) setOpen(false);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      const p = parseDate(value);
      if (p) {
        setYs(String(p.y));
        setMs(pad(p.m));
        setDs(pad(p.d));
      } else {
        setYs("");
        setMs("");
        setDs("");
      }
      setError(false);
      setOpen(false);
      target.blur();
      return;
    }
  }

  function onSegBlur() {
    // 다음 element가 wrapper 안인지 보고 외부로 나갈 때만 commit
    setTimeout(() => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(document.activeElement)) {
        commit();
      }
    }, 0);
  }

  // 캘린더에서 날짜 선택
  function selectDate(s: string) {
    if (min && cmp(s, min) < 0) return;
    if (max && cmp(s, max) > 0) return;
    editingRef.current = false;
    const p = parseDate(s)!;
    setYs(String(p.y));
    setMs(pad(p.m));
    setDs(pad(p.d));
    setError(false);
    if (s !== value) onChange(s);
    setOpen(false);
  }

  function navPopup(delta: number) {
    let { y, m } = popupYM;
    m += delta;
    while (m < 1) { m += 12; y--; }
    while (m > 12) { m -= 12; y++; }
    setPopupYM({ y, m });
  }

  // 캘린더 셀
  const cells = useMemo(
    () => buildCalendarCells(popupYM.y, popupYM.m),
    [popupYM],
  );

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = minYear; y <= maxY; y++) arr.push(y);
    return arr;
  }, [minYear, maxY]);

  return (
    <div
      className={`date-picker${fullWidth ? " is-full" : ""}${error ? " is-error" : ""}${disabled ? " is-disabled" : ""}`}
      ref={wrapRef}
    >
      <div className="dp-header">
        <input
          ref={yRef}
          id={id}
          aria-label={ariaLabel ?? "연도"}
          className="dp-seg dp-seg-y"
          inputMode="numeric"
          maxLength={4}
          value={ys}
          placeholder="YYYY"
          disabled={disabled}
          onChange={(e) => onSegChange("y", e.target.value)}
          onKeyDown={(e) => onSegKeyDown("y", e)}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          onBlur={onSegBlur}
        />
        <span className="dp-sep">/</span>
        <input
          ref={mRef}
          aria-label="월"
          className="dp-seg dp-seg-m"
          inputMode="numeric"
          maxLength={2}
          value={ms}
          placeholder="MM"
          disabled={disabled}
          onChange={(e) => onSegChange("m", e.target.value)}
          onKeyDown={(e) => onSegKeyDown("m", e)}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          onBlur={onSegBlur}
        />
        <span className="dp-sep">/</span>
        <input
          ref={dRef}
          aria-label="일"
          className="dp-seg dp-seg-d"
          inputMode="numeric"
          maxLength={2}
          value={ds}
          placeholder="DD"
          disabled={disabled}
          onChange={(e) => onSegChange("d", e.target.value)}
          onKeyDown={(e) => onSegKeyDown("d", e)}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          onBlur={onSegBlur}
        />
        <button
          type="button"
          className="dp-toggle"
          aria-label="캘린더 열기"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="calendar" size={16} />
        </button>
      </div>

      {open && popupPos && createPortal(
        <div
          ref={popupRef}
          className="dp-popup is-portal"
          style={{
            position: "fixed",
            top: popupPos.top,
            left: popupPos.left,
          }}
        >
          <div className="dp-cal">
            <div className="dp-cal-hd">
              <button type="button" className="dp-nav" onClick={() => navPopup(-1)} aria-label="이전 달">‹</button>
              <CompactSelect
                value={popupYM.y}
                options={years.map((y) => ({ value: y, label: String(y) }))}
                onChange={(v) => setPopupYM({ ...popupYM, y: v })}
                ariaLabel="연도"
              />
              <CompactSelect
                value={popupYM.m}
                options={Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => ({
                  value: mm,
                  label: pad(mm),
                }))}
                onChange={(v) => setPopupYM({ ...popupYM, m: v })}
                ariaLabel="월"
              />
              <button type="button" className="dp-nav" onClick={() => navPopup(1)} aria-label="다음 달">›</button>
            </div>
            <div className="dp-weekdays">
              {WEEKDAYS.map((w, i) => (
                <span key={w} className={`dp-wd${i === 0 ? " is-sun" : ""}${i === 6 ? " is-sat" : ""}`}>{w}</span>
              ))}
            </div>
            <div className="dp-grid">
              {cells.map((c, i) => {
                const s = fmtDate(c.y, c.m, c.d);
                const wd = i % 7;
                const cls = ["dp-cell"];
                if (c.outside) cls.push("is-outside");
                if (s === today) cls.push("is-today");
                if (s === value) cls.push("is-selected");
                if (wd === 0) cls.push("is-sun");
                if (wd === 6) cls.push("is-sat");
                // 지정 휴일(공휴일/회사휴일) 마킹 — 본 월(c.outside 제외)만
                const holiday = !c.outside ? holidayLabel?.(s) ?? null : null;
                if (holiday) cls.push("is-off");
                const disabledCell = (min && cmp(s, min) < 0) || (max && cmp(s, max) > 0);
                if (disabledCell) cls.push("is-disabled");
                return (
                  <button
                    key={`${s}-${i}`}
                    type="button"
                    className={cls.join(" ")}
                    disabled={!!disabledCell}
                    title={holiday ?? undefined}
                    onClick={() => selectDate(s)}
                  >
                    {c.d}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="dp-today-btn"
              onClick={() => {
                if (max && cmp(today, max) > 0) return;
                if (min && cmp(today, min) < 0) return;
                selectDate(today);
              }}
            >
              오늘
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
